import { useState, useEffect, useCallback } from "react";

// ─── Data ─────────────────────────────────────────────────────────────────────

const AVATAR_TYPES = [
  { id: "executive", label: "Executive", desc: "Corporate leader", icon: "ti-briefcase", code: "EXEC_001" },
  { id: "teacher",   label: "Teacher",   desc: "Expert educator",  icon: "ti-school"  ,  code: "TEACH_002"  },
  { id: "creative",  label: "Creative",  desc: "Innovative thinker", icon: "ti-palette" },
  { id: "analyst",   label: "Analyst",   desc: "Data-driven pro",  icon: "ti-chart-bar" },
];

const MOODS = [
  { id: "professional", label: "Professional", color: "#4338CA", bg: "#EEF2FF", desc: "Calm, authoritative" },
  { id: "energetic",    label: "Energetic",    color: "#DC2626", bg: "#FEF2F2", desc: "High energy, dynamic" },
  { id: "friendly",     label: "Friendly",     color: "#16A34A", bg: "#F0FDF4", desc: "Warm, approachable" },
  { id: "formal",       label: "Formal",       color: "#1E293B", bg: "#F8FAFC", desc: "Serious, precise" },
  { id: "casual",       label: "Casual",       color: "#D97706", bg: "#FFFBEB", desc: "Relaxed, easy-going" },
];

// Numeric expression params per mood — all interpolatable
const EXPR = {
  professional: { eyeRY: 6.5, browLift: -3, smileD: 6,  smileW: 16, mouthY: 112 },
  energetic:    { eyeRY: 9.5, browLift: -9, smileD: 17, smileW: 23, mouthY: 108 },
  friendly:     { eyeRY: 5.0, browLift: -5, smileD: 10, smileW: 18, mouthY: 110 },
  formal:       { eyeRY: 5.5, browLift:  2, smileD:  0, smileW: 12, mouthY: 113 },
  casual:       { eyeRY: 4.5, browLift:  1, smileD:  7, smileW: 15, mouthY: 113 },
};

const HAIR = {
  executive: {
    main: "M40 78 Q44 30 90 25 Q136 30 140 78 Q128 42 90 38 Q52 42 40 78Z",
    extra: null,
  },
  teacher: {
    main: "M36 86 Q38 24 90 18 Q142 24 144 86 Q138 37 90 33 Q42 37 36 86Z",
    extra: "M36 86 Q25 108 28 132 Q39 104 36 86Z M144 86 Q155 108 152 132 Q141 104 144 86Z",
  },
  creative: {
    main: "M38 76 Q40 20 90 15 Q140 20 142 76 Q136 28 90 26 Q44 28 38 76Z",
    extra: "M34 78 Q22 98 26 126 Q38 96 34 78Z M146 78 Q158 98 154 126 Q142 96 146 78Z M46 36 Q40 17 51 11 Q58 26 50 38Z M134 36 Q140 17 129 11 Q122 26 130 38Z",
  },
  analyst: {
    main: "M42 76 Q46 32 90 27 Q134 32 138 76 Q126 44 90 40 Q54 44 42 76Z",
    extra: null,
  },
};

const SKIN_TONES   = ["#FDDBB4","#F5C28A","#E0945A","#B5651D","#7B3F00","#3D1C0A"];
const HAIR_COLORS  = ["#1C1008","#3D1F0A","#6B3A1F","#C8882A","#B8B8B8","#8B3060"];
const OUTFIT_COLORS= ["#1E3A5F","#1B4332","#581C87","#7F1D1D","#1C3557","#1A1A2E"];

// ─── Helper: lerp between two expression sets ─────────────────────────────────
function lerp(a, b, t) { return a + (b - a) * t; }
function lerpExpr(from, to, t) {
  return Object.fromEntries(
    Object.keys(from).map(k => [k, lerp(from[k], to[k], t)])
  );
}

// ─── Avatar SVG ───────────────────────────────────────────────────────────────
function AvatarSVG({ avatarType, expr, skinTone, hairColor, outfitColor, blink, moodBg }) {
  const { eyeRY, browLift, smileD, smileW, mouthY } = expr;
  const hair = HAIR[avatarType] || HAIR.executive;

  const ley = 85, rex = 115, ley2 = 65;
  const eyRY = blink ? 0.4 : eyeRY;
  const browBase = 70;
  const bY = browBase + browLift;

  const mouthPath  = `M${90 - smileW} ${mouthY} Q90 ${mouthY + smileD} ${90 + smileW} ${mouthY}`;
  const lBrow = `M${ley2 - 13} ${bY + 2} Q${ley2} ${bY} ${ley2 + 13} ${bY + 2}`;
  const rBrow = `M${rex - 13} ${bY + 2} Q${rex} ${bY} ${rex + 13} ${bY + 2}`;

  const noseFill = skinTone === "#FDDBB4" || skinTone === "#F5C28A" ? "#D4855A" : "#7B4020";

  return (
    <svg viewBox="0 0 180 225" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%", display: "block" }}>
      {/* Mood glow bg */}
      <circle cx="90" cy="105" r="83" fill={moodBg} />

      {/* Shoulders body */}
      <path d="M 58 168 Q 32 184 22 225 L 158 225 Q 148 184 122 168 Z" fill={outfitColor} />

      {/* Collar / shirt */}
      <path d="M 74 166 L 90 182 L 106 166 L 113 160 L 90 174 L 67 160 Z" fill="white" opacity="0.92" />

      {/* Neck */}
      <rect x="79" y="146" width="22" height="24" fill={skinTone} rx="2" />

      {/* Head */}
      <ellipse cx="90" cy="90" rx="53" ry="58" fill={skinTone} />

      {/* Hair back */}
      <path d={hair.main} fill={hairColor} />
      {hair.extra && <path d={hair.extra} fill={hairColor} />}

      {/* Ears */}
      <ellipse cx="37" cy="93" rx="7" ry="10" fill={skinTone} />
      <ellipse cx="143" cy="93" rx="7" ry="10" fill={skinTone} />

      {/* Eye whites */}
      <ellipse cx={ley2} cy={ley} rx="10.5" ry={eyRY} fill="white" style={{ transition: "ry 0.25s ease" }} />
      <ellipse cx={rex}  cy={ley} rx="10.5" ry={eyRY} fill="white" style={{ transition: "ry 0.25s ease" }} />

      {!blink && (
        <>
          {/* Irises */}
          <ellipse cx={ley2} cy={ley + 1} rx={5.5} ry={Math.max(eyRY * 0.72, 0.5)} fill="#3A2218" />
          <ellipse cx={rex}  cy={ley + 1} rx={5.5} ry={Math.max(eyRY * 0.72, 0.5)} fill="#3A2218" />
          {/* Pupils */}
          <circle cx={ley2} cy={ley + 1} r="3" fill="#0D0D0D" />
          <circle cx={rex}  cy={ley + 1} r="3" fill="#0D0D0D" />
          {/* Shine */}
          <circle cx={ley2 + 3} cy={ley - 2} r="1.8" fill="white" />
          <circle cx={rex  + 3} cy={ley - 2} r="1.8" fill="white" />
        </>
      )}

      {/* Eyebrows */}
      <path d={lBrow} stroke={hairColor} strokeWidth="3.5" fill="none" strokeLinecap="round" />
      <path d={rBrow} stroke={hairColor} strokeWidth="3.5" fill="none" strokeLinecap="round" />

      {/* Nose */}
      <ellipse cx="90" cy="103" rx="4" ry="6.5" fill="none" stroke={noseFill} strokeWidth="1.2" />
      <path d="M84 108.5 Q90 113 96 108.5" fill="none" stroke={noseFill} strokeWidth="1.4" strokeLinecap="round" />

      {/* Mouth */}
      <path d={mouthPath} stroke="#A0404A" strokeWidth="3" fill="none" strokeLinecap="round" />

      {/* Teeth for big smiles */}
      {smileD > 11 && (
        <path
          d={`M${90 - smileW + 4} ${mouthY + 2} Q90 ${mouthY + smileD * 0.55} ${90 + smileW - 4} ${mouthY + 2}`}
          fill="white"
        />
      )}
    </svg>
  );
}

// ─── Swatch picker ─────────────────────────────────────────────────────────────
function Swatches({ label, options, value, onChange, accentColor }) {
  return (
    <div style={{ marginBottom: "12px" }}>
      <span style={{ fontSize: "11px", fontWeight: 500, color: "var(--color-text-secondary)", display: "block", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</span>
      <div style={{ display: "flex", gap: "7px", flexWrap: "wrap" }}>
        {options.map(c => (
          <div
            key={c}
            onClick={() => onChange(c)}
            title={c}
            style={{
              width: "26px", height: "26px",
              borderRadius: "50%",
              background: c,
              cursor: "pointer",
              border: value === c ? `2px solid ${accentColor}` : "2px solid transparent",
              outline: value === c ? "2px solid var(--color-background-primary)" : "none",
              outlineOffset: "-2px",
              transform: value === c ? "scale(1.18)" : "scale(1)",
              transition: "transform 0.15s, border 0.15s",
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Output block ─────────────────────────────────────────────────────────────
function OutputBlock({ content, copied, copyKey, onCopy, mono = false }) {
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "6px" }}>
        <button
          onClick={() => onCopy(typeof content === "object" ? JSON.stringify(content, null, 2) : content, copyKey)}
          style={{ padding: "4px 10px", borderRadius: "var(--border-radius-md)", border: "0.5px solid var(--color-border-secondary)", background: "transparent", fontSize: "11px", color: "var(--color-text-secondary)", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}
        >
          <i className={`ti ${copied === copyKey ? "ti-check" : "ti-copy"}`} aria-hidden="true" style={{ fontSize: "12px" }} />
          {copied === copyKey ? "Copied!" : "Copy"}
        </button>
      </div>
      <div style={{
        padding: "12px", background: "var(--color-background-secondary)", borderRadius: "var(--border-radius-md)",
        fontSize: "12.5px", lineHeight: "1.65", color: "var(--color-text-primary)",
        fontFamily: mono ? "var(--font-mono)" : "var(--font-sans)",
        whiteSpace: mono ? "pre" : "normal", overflowX: mono ? "auto" : "visible",
        maxHeight: "220px", overflowY: "auto",
      }}>
        {typeof content === "object" ? JSON.stringify(content, null, 2) : content}
      </div>
    </div>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────────
export default function AvatarCreator() {
  const [avatarType, setAvatarType] = useState("executive");
  const [mood, setMood]             = useState("professional");
  const [prevMood, setPrevMood]     = useState("professional");
  const [moodT, setMoodT]           = useState(1);         // interpolation 0→1
  const [skinTone, setSkinTone]     = useState(SKIN_TONES[0]);
  const [hairColor, setHairColor]   = useState(HAIR_COLORS[0]);
  const [outfitColor, setOutfitColor] = useState(OUTFIT_COLORS[0]);
  const [blink, setBlink]           = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [output, setOutput]         = useState(null);
  const [outputTab, setOutputTab]   = useState("dalle");
  const [copied, setCopied]         = useState(null);

  const currentMood = MOODS.find(m => m.id === mood) || MOODS[0];
  const moodColor = currentMood.color;

  // Smooth mood transition
  const handleMoodChange = useCallback((newMood) => {
    if (newMood === mood) return;
    setPrevMood(mood);
    setMood(newMood);
    setMoodT(0);
  }, [mood]);

  useEffect(() => {
    if (moodT >= 1) return;
    const raf = requestAnimationFrame(() => setMoodT(t => Math.min(t + 0.08, 1)));
    return () => cancelAnimationFrame(raf);
  }, [moodT]);

  // Interpolated expression
  const fromExpr = EXPR[prevMood] || EXPR.professional;
  const toExpr   = EXPR[mood]     || EXPR.professional;
  const liveExpr = lerpExpr(fromExpr, toExpr, moodT);

  // Blink timer
  useEffect(() => {
    const blinkEvery = mood === "energetic" ? 1800 : mood === "formal" ? 4200 : 2800;
    const id = setInterval(() => {
      setBlink(true);
      setTimeout(() => setBlink(false), 130);
    }, blinkEvery);
    return () => clearInterval(id);
  }, [mood]);

  // ── Generate ────────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    setIsGenerating(true);
    setOutput(null);

    const aData = AVATAR_TYPES.find(a => a.id === avatarType);
    const mData = MOODS.find(m => m.id === mood);

    const prompt = `You are a creative director for AI-generated presentation avatars. Return ONLY valid JSON with no markdown fences.

Avatar spec:
- Type: ${aData?.label} (${aData?.desc})
- Mood: ${mData?.label} (${mData?.desc})
- Skin tone: ${skinTone}
- Hair color: ${hairColor}
- Outfit color: ${outfitColor}

Return this exact JSON shape:
{
  "dalle_prompt": "detailed DALL-E 3 photorealistic portrait prompt (120+ words): appearance, expression, lighting, studio background, professional quality for business presentation slides",
  "heygen_config": {
    "avatar_style": "realistic",
    "background": "background description",
    "voice_style": "voice tone description",
    "gesture_style": "body language description",
    "script_example": "2-3 sentence example opening for a business presentation"
  },
  "runway_prompt": "Runway Gen-3 / Sora video generation prompt (60-80 words) of avatar talking in professional setting",
  "did_config": {
    "driver": "talking_head",
    "expression": "${mood}",
    "pose": "pose description",
    "movement_intensity": "low or medium or high"
  },
  "persona": "2-3 sentence personality description for this avatar to give as context to any AI presenter tool"
}`;

    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          messages: [{ role: "user", content: prompt }],
        }),
      });
      const data = await res.json();
      const raw = (data.content?.[0]?.text || "").replace(/```json|```/g, "").trim();
      setOutput(JSON.parse(raw));
    } catch (e) {
      setOutput({ _error: "Generation failed — please try again." });
    } finally {
      setIsGenerating(false);
    }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const OUTPUT_TABS = [
    { key: "dalle",   label: "DALL-E 3",    content: output?.dalle_prompt,  mono: false },
    { key: "heygen",  label: "HeyGen",      content: output?.heygen_config, mono: true  },
    { key: "runway",  label: "Runway/Sora", content: output?.runway_prompt, mono: false },
    { key: "did",     label: "D-ID",        content: output?.did_config,    mono: true  },
    { key: "persona", label: "Persona",     content: output?.persona,       mono: false },
  ];

  return (
    <div style={{ fontFamily: "var(--font-sans)", padding: "1rem 0" }}>
      <h2 className="sr-only">Dynamic avatar creator for AI video presentations</h2>

      <div style={{ display: "grid", gridTemplateColumns: "252px 1fr", gap: "20px", alignItems: "start" }}>

        {/* ─── Left controls ──────────────────────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

          {/* Avatar type */}
          <section>
            <p style={{ fontSize: "11px", fontWeight: 500, color: "var(--color-text-secondary)", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.07em" }}>Avatar type</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
              {AVATAR_TYPES.map(t => (
                <button
                  key={t.id}
                  onClick={() => setAvatarType(t.id)}
                  style={{
                    padding: "9px 8px",
                    borderRadius: "var(--border-radius-md)",
                    border: `0.5px solid ${avatarType === t.id ? moodColor : "var(--color-border-tertiary)"}`,
                    background: avatarType === t.id ? `${moodColor}10` : "var(--color-background-primary)",
                    cursor: "pointer", textAlign: "left", transition: "all 0.15s",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "5px", marginBottom: "2px" }}>
                    <i className={`ti ${t.icon}`} aria-hidden="true" style={{ fontSize: "13px", color: avatarType === t.id ? moodColor : "var(--color-text-secondary)" }} />
                    <span style={{ fontSize: "12.5px", fontWeight: 500, color: avatarType === t.id ? moodColor : "var(--color-text-primary)" }}>{t.label}</span>
                  </div>
                  <div style={{ fontSize: "11px", color: "var(--color-text-tertiary)" }}>{t.desc}</div>
                </button>
              ))}
            </div>
          </section>

          {/* Mood */}
          <section>
            <p style={{ fontSize: "11px", fontWeight: 500, color: "var(--color-text-secondary)", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.07em" }}>Mood & expression</p>
            <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
              {MOODS.map(m => (
                <button
                  key={m.id}
                  onClick={() => handleMoodChange(m.id)}
                  style={{
                    padding: "9px 11px",
                    borderRadius: "var(--border-radius-md)",
                    border: `0.5px solid ${mood === m.id ? m.color : "var(--color-border-tertiary)"}`,
                    background: mood === m.id ? `${m.color}0D` : "var(--color-background-primary)",
                    cursor: "pointer", display: "flex", justifyContent: "space-between",
                    alignItems: "center", transition: "all 0.15s",
                  }}
                >
                  <span style={{ fontSize: "13px", fontWeight: mood === m.id ? 500 : 400, color: mood === m.id ? m.color : "var(--color-text-primary)" }}>{m.label}</span>
                  <span style={{ fontSize: "11px", color: "var(--color-text-tertiary)" }}>{m.desc}</span>
                </button>
              ))}
            </div>
          </section>

          {/* Colors */}
          <section>
            <p style={{ fontSize: "11px", fontWeight: 500, color: "var(--color-text-secondary)", marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.07em" }}>Appearance</p>
            <Swatches label="Skin"   options={SKIN_TONES}    value={skinTone}    onChange={setSkinTone}    accentColor={moodColor} />
            <Swatches label="Hair"   options={HAIR_COLORS}   value={hairColor}   onChange={setHairColor}   accentColor={moodColor} />
            <Swatches label="Outfit" options={OUTFIT_COLORS} value={outfitColor} onChange={setOutfitColor} accentColor={moodColor} />
          </section>
        </div>

        {/* ─── Right: preview + generate ──────────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>

          {/* Avatar card */}
          <div style={{
            background: `${moodColor}08`,
            border: `0.5px solid ${moodColor}30`,
            borderRadius: "var(--border-radius-lg)",
            padding: "20px 20px 16px",
            display: "flex", flexDirection: "column", alignItems: "center", gap: "10px",
            transition: "background 0.4s, border-color 0.4s",
          }}>
            {/* Mood badge */}
            <div style={{
              background: `${moodColor}18`,
              border: `0.5px solid ${moodColor}40`,
              borderRadius: "20px", padding: "3px 14px",
              fontSize: "11.5px", fontWeight: 500, color: moodColor,
              transition: "all 0.3s",
            }}>
              {currentMood.label} mode
            </div>

            {/* Avatar */}
            <div style={{
              width: "170px", height: "200px",
              animation: mood === "energetic" ? "bounce 1.2s ease-in-out infinite" :
                         mood === "friendly"  ? "nod 2.8s ease-in-out infinite" :
                         mood === "casual"    ? "sway 3.5s ease-in-out infinite" :
                                               "breathe 4s ease-in-out infinite",
            }}>
              <AvatarSVG
                avatarType={avatarType}
                expr={liveExpr}
                skinTone={skinTone}
                hairColor={hairColor}
                outfitColor={outfitColor}
                blink={blink}
                moodBg={currentMood.bg}
              />
            </div>

            {/* Name row */}
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "14px", fontWeight: 500, color: "var(--color-text-primary)" }}>
                {AVATAR_TYPES.find(a => a.id === avatarType)?.label}
              </div>
              <div style={{ fontSize: "11.5px", color: "var(--color-text-secondary)", marginTop: "2px" }}>
                {AVATAR_TYPES.find(a => a.id === avatarType)?.desc}
              </div>
            </div>
          </div>

          {/* Generate button */}
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            style={{
              padding: "11px 16px", borderRadius: "var(--border-radius-md)",
              border: `0.5px solid ${moodColor}`,
              background: moodColor, color: "white",
              fontSize: "13.5px", fontWeight: 500,
              cursor: isGenerating ? "not-allowed" : "pointer",
              opacity: isGenerating ? 0.75 : 1,
              display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
              transition: "all 0.2s",
            }}
          >
            {isGenerating ? (
              <>
                <span style={{ width: "13px", height: "13px", border: "2px solid rgba(255,255,255,0.35)", borderTopColor: "white", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
                Generating prompts…
              </>
            ) : (
              <>
                <i className="ti ti-wand" aria-hidden="true" style={{ fontSize: "16px" }} />
                Generate AI video prompts ↗
              </>
            )}
          </button>

          <p style={{ fontSize: "11px", color: "var(--color-text-tertiary)", textAlign: "center", margin: 0 }}>
            Outputs ready for DALL-E 3 · HeyGen · D-ID · Runway / Sora
          </p>
        </div>
      </div>

      {/* ─── Output panel ─────────────────────────────────────────── */}
      {output && (
        <div style={{ marginTop: "20px", border: "0.5px solid var(--color-border-tertiary)", borderRadius: "var(--border-radius-lg)", overflow: "hidden" }}>
          {output._error ? (
            <div style={{ padding: "1rem", color: "var(--color-text-danger)", fontSize: "13px" }}>{output._error}</div>
          ) : (
            <>
              {/* Tabs */}
              <div style={{ display: "flex", borderBottom: "0.5px solid var(--color-border-tertiary)", background: "var(--color-background-secondary)", overflowX: "auto" }}>
                {OUTPUT_TABS.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setOutputTab(tab.key)}
                    style={{
                      padding: "10px 16px", border: "none", background: "transparent",
                      fontSize: "12.5px", fontWeight: outputTab === tab.key ? 500 : 400,
                      color: outputTab === tab.key ? moodColor : "var(--color-text-secondary)",
                      cursor: "pointer", whiteSpace: "nowrap",
                      borderBottom: outputTab === tab.key ? `2px solid ${moodColor}` : "2px solid transparent",
                      transition: "all 0.15s",
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              {/* Content */}
              <div style={{ padding: "14px 16px" }}>
                {OUTPUT_TABS.map(tab => outputTab === tab.key && tab.content && (
                  <OutputBlock
                    key={tab.key}
                    content={tab.content}
                    copyKey={tab.key}
                    copied={copied}
                    onCopy={copy}
                    mono={tab.mono}
                  />
                ))}

                {/* Script example callout for HeyGen */}
                {outputTab === "heygen" && output.heygen_config?.script_example && (
                  <div style={{ marginTop: "12px", padding: "12px 14px", background: `${moodColor}08`, border: `0.5px solid ${moodColor}30`, borderRadius: "var(--border-radius-md)", fontSize: "13px", fontStyle: "italic", color: "var(--color-text-primary)", lineHeight: "1.6" }}>
                    <span style={{ fontStyle: "normal", fontSize: "11px", fontWeight: 500, color: moodColor, display: "block", marginBottom: "5px" }}>Example script</span>
                    "{output.heygen_config.script_example}"
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes bounce  { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
        @keyframes nod     { 0%,100% { transform: rotate(0deg); } 50% { transform: rotate(-2deg); } }
        @keyframes sway    { 0%,100% { transform: rotate(-2deg); } 50% { transform: rotate(2deg); } }
        @keyframes breathe { 0%,100% { transform: scale(1); } 50% { transform: scale(1.012); } }
        .sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); border: 0; }
      `}</style>
    </div>
  );
}